import { useEffect, useState } from 'react';
import { supabase } from '@mondivago/supabase';
import type { Country } from '@mondivago/types';

export function useCountries(activeOnly = true) {
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchCountries() {
      try {
        let query = supabase
          .from('content_countries')
          .select('id, name, slug, iso2, is_active, cities:content_cities(id, tours!inner(count))');

        if (activeOnly) {
          query = query.eq('is_active', true);
        }

        let { data, error } = await query;

        if (error) throw error;

        // Fallback to all countries if activeOnly returned empty
        if (activeOnly && (!data || data.length === 0)) {
          const { data: allData, error: allErr } = await supabase
            .from('content_countries')
            .select('id, name, slug, iso2, is_active, cities:content_cities(id, tours(count))');
          if (!allErr && allData) {
            data = allData;
          }
        }

        const processedCountries: Country[] = (data || []).map((country) => {
          const totalTours = (country.cities || []).reduce((sum: number, city) => {
            const count = city.tours?.[0]?.count || 0;
            return sum + count;
          }, 0);
          return {
            id: country.id,
            name: country.name,
            slug: country.slug,
            iso2: country.iso2 || undefined,
            is_active: country.is_active || false,
            activity_count: totalTours,
          };
        });

        processedCountries.sort((a, b) => {
          if (b.activity_count !== a.activity_count) {
            return b.activity_count - a.activity_count;
          }
          return a.name.localeCompare(b.name);
        });

        setCountries(processedCountries);
      } catch (err) {
        setError('Failed to fetch countries. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    void fetchCountries();
  }, [activeOnly]);

  return { countries, loading, error };
}
